import { createElement } from 'react';
import { Routes, Route, Link, useLocation, useParams } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { PageHeader, Section, Breadcrumbs, BreadcrumbItem } from '@vnetwork/ui/layout';
import { StatusPill } from '@vnetwork/ui/data';
import { Skeleton } from '@vnetwork/ui/primitives';
import { CompactStat, CompactStatGroup } from '../components/CompactStat';
import { useAesHealth, useAesMetrics, sumBy } from '@/api/aes';
import { AES_NAV_ENTRIES, useAesContextualNav } from './aes-nav';
import { AesLogViewer } from './AesLogViewer';
import { AesSessions } from './AesSessions';
import { AesScrambler } from './AesScrambler';
import { AesSettings } from './AesSettings';
import { UsageAnalytics } from './UsageAnalytics';
import { SelfHostedOverview } from './drm/SelfHostedOverview';

/** Compact uptime string from seconds. */
function fmtUptime(s?: number): string {
  if (!s || s < 0) return '—';
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d) return `${d}d ${h}h`;
  if (h) return `${h}h ${m}m`;
  return `${m}m`;
}

/**
 * AES gateway ops console — HLS AES-128 key gateway (VOD + LIVE), backed by the
 * gateway's own /healthz, /metricsz and /logz. Sub-pages (Overview / Sessions /
 * Scrambler / Live logs) live in the app's ContextualSidebar, same scope-swap
 * model as the CDN / Compute / Kubernetes consoles.
 */
export function AesConsole() {
  const { pathname } = useLocation();
  const { serviceId } = useParams<{ serviceId: string }>();
  const health = useAesHealth(serviceId);
  const base = `/services/aes/${serviceId}`;

  // Active sub-page → drives a per-page header (title/description/icon/breadcrumbs),
  // matching the CDN console where the header changes per view instead of staying
  // fixed on the service name.
  const sub = pathname.slice(base.length).split('/').filter(Boolean)[0] ?? '';
  const nav = AES_NAV_ENTRIES.find((n) => n.slug === sub) ?? AES_NAV_ENTRIES[0];
  const isRoot = nav.slug === '';
  const HeaderIcon = isRoot ? ShieldCheck : nav.icon;

  // One unified sidebar for the whole AES service (gateway + DRM). DrmConsole
  // sets the SAME nav, so navigating into /drm/* keeps this menu stable.
  useAesContextualNav(serviceId);

  return (
    <>
      <PageHeader
        icon={createElement(HeaderIcon, { size: 20 })}
        breadcrumbs={
          // Two DIRECT children so Breadcrumbs injects the separator between them
          // (a wrapping Fragment collapses to one child → no separator).
          <Breadcrumbs>
            {isRoot ? (
              <BreadcrumbItem asChild><Link to="/services">Services</Link></BreadcrumbItem>
            ) : (
              <BreadcrumbItem asChild><Link to={base}>AES Gateway</Link></BreadcrumbItem>
            )}
            <BreadcrumbItem current>{isRoot ? 'AES Gateway' : nav.label}</BreadcrumbItem>
          </Breadcrumbs>
        }
        title={isRoot ? 'AES Gateway' : nav.label}
        description={nav.description}
        actions={
          <StatusPill tone={health.data?.ok ? 'success' : health.isLoading ? 'muted' : 'danger'} loading={health.isLoading}>
            {health.data?.ok ? 'Online' : health.isLoading ? 'Checking' : 'Unreachable'}
          </StatusPill>
        }
      />

      <Routes>
        <Route index element={<AesOverview serviceId={serviceId} base={base} />} />
        <Route
          path="sessions"
          element={
            <Section>
              <AesSessions serviceId={serviceId} />
            </Section>
          }
        />
        <Route
          path="scrambler"
          element={
            <Section>
              <AesScrambler serviceId={serviceId} />
            </Section>
          }
        />
        <Route
          path="usage"
          element={
            <Section>
              <UsageAnalytics serviceId={serviceId} />
            </Section>
          }
        />
        <Route
          path="logs"
          element={
            <Section>
              <AesLogViewer serviceId={serviceId} />
            </Section>
          }
        />
        <Route
          path="settings"
          element={
            <Section>
              <AesSettings serviceId={serviceId} />
            </Section>
          }
        />
        <Route path="*" element={<AesOverview serviceId={serviceId} base={base} />} />
      </Routes>
    </>
  );
}

/**
 * Merged service Overview — ONE page (no tabs): gateway runtime counters up top,
 * then the self-hosted streams data-plane (health strip + analytics + recent
 * streams). The old per-mode overviews and the Operations jump-off cards are gone
 * — navigation lives entirely in the unified sidebar.
 */
function AesOverview({ serviceId, base }: { serviceId?: string; base: string }) {
  // Gateway RUNTIME ops (requests/5xx/active/uptime) from @aes /metricsz.
  const metrics = useAesMetrics(serviceId);

  const m = metrics.data;
  const requests = m?.requests.total ?? 0;
  const errors5xx = sumBy(m?.requests.by_status, (k) => k.startsWith('5'));
  const active = sumBy(m?.sessions.active);

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Section title="Gateway" style={{ marginBottom: 0 }}>
        {metrics.isLoading ? (
          <Skeleton variant="rect" height={72} />
        ) : (
          <CompactStatGroup columns={4}>
            <CompactStat label="Requests" value={requests.toLocaleString()} />
            <CompactStat
              label="5xx responses"
              value={errors5xx.toLocaleString()}
              dotColor={errors5xx > 0 ? 'var(--vn-danger-solid)' : undefined}
              hint={errors5xx > 0 ? 'upstream/serve errors' : 'healthy'}
            />
            <CompactStat label="Active sessions" value={active.toLocaleString()} />
            <CompactStat label="Uptime" value={fmtUptime(m?.uptime_seconds)} />
          </CompactStatGroup>
        )}
      </Section>

      <Section title="Self-hosted streams (BETA)" style={{ marginBottom: 0 }}>
        <SelfHostedOverview serviceId={serviceId} base={`${base}/drm`} />
      </Section>
    </div>
  );
}

export default AesConsole;
